import { createApi, fetchBaseQuery, retry } from '@reduxjs/toolkit/query/react';
import { msalInstance } from '@versita/frontend/auth-web';
import environment from '../environments/environment';
import { BaseQueryApi, FetchArgs } from '@reduxjs/toolkit/dist/query/react';
import { RetryOptions } from '@reduxjs/toolkit/dist/query/retry';

type Empty = Record<never, never>;

const baseQuery = retry(
  fetchBaseQuery({
    baseUrl: environment.apis.graphApi.endpoint,
    isJsonContentType: () => false,
    prepareHeaders: async (headers) => {
      try {
        const account = msalInstance.getActiveAccount();
        if (account) {
          const { accessToken } = await msalInstance.acquireTokenSilent({
            scopes: environment.apis.graphApi.scopes,
          });
          if (accessToken) {
            headers.set('Authorization', `Bearer ${accessToken}`);
          }
        }
        return headers;
      } catch {
        try {
          msalInstance.acquireTokenRedirect({
            scopes: environment.apis.graphApi.scopes,
          });
        } catch {
          const { accessToken } = await msalInstance.acquireTokenPopup({
            scopes: environment.apis.graphApi.scopes,
          });
          if (accessToken) {
            headers.set('Authorization', `Bearer ${accessToken}`);
          }
        }
      }
    },
  }),
  { maxRetries: 3 },
);
const baseQueryWithInterceptor = async (
  args: FetchArgs,
  api: BaseQueryApi,
  extraOptions: RetryOptions,
) => {
  const result = await baseQuery(args, api, extraOptions);

  if (result.error && result.error.status === 404) {
    // Handle 404 by setting data to null
    return { data: null };
  }

  return result;
};

export const graphApi = createApi({
  reducerPath: 'graphApi',
  baseQuery: baseQueryWithInterceptor,
  endpoints: (builder) => ({
    getUserPhoto: builder.query<string, void | Empty>({
      query: () => {
        return {
          url: 'me/photo/$value',
          method: 'GET',
          responseHandler: async (res: Response) => {
            const blob = await res.blob();
            return URL.createObjectURL(blob);
          },
        };
      },
    }),
    getUserPhotoDetails: builder.query<
      { height: number; width: number },
      void | Empty
    >({
      query: () => {
        return {
          url: 'me/photo',
          method: 'GET',
        };
      },
    }),
  }),
});

export const {
  useGetUserPhotoQuery,
  useGetUserPhotoDetailsQuery,
  endpoints: { getUserPhoto, getUserPhotoDetails },
} = graphApi;
